/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import useRazorpay, { RazorpayOptions } from "react-razorpay";

import PurchasePlanApi from "../api/PurchasePlanApi";
import { appConstants } from "../config/app-constants";
import { EActionItem, EToastvariant, ICreateEFPlanTxn } from "../config/interfaces";
import { useAppDispatch, useAppSelector } from "../redux/Store";
import { loggedInUserSelector } from "../redux/slice/LoggedInUserSlice";
import { showToastMsg } from "../redux/slice/ToastMsgSlice";
import { OverlaySpinner } from "./placeholders";

type Props = {
	filingPlanData: ICreateEFPlanTxn | null;
	loadRazorpay: boolean;
	handleOnPaymentSuccess: () => void;
	handleOnPaymentCancel: () => void;
	handleErrorOnInit: () => void;
	key?: any;
};

const RazorPayPayment: React.FC<Props> = (props: Props) => {
	const {
		filingPlanData,
		loadRazorpay,
		handleOnPaymentSuccess,
		handleOnPaymentCancel,
		handleErrorOnInit
	} = props;

	const dispatch = useAppDispatch();
	const { loggedInUser } = useAppSelector(loggedInUserSelector);

	const [Razorpay] = useRazorpay();

	const [isProcessing, setIsProcessing] = useState<EActionItem>(EActionItem.NULL);

	const handlePayment = useCallback(async () => {
		try {
			if (!filingPlanData) {
				return;
			}

			setIsProcessing(() => EActionItem.PAYMENT);

			const plansApi = PurchasePlanApi.getInstance();
			const { orderData } = await plansApi.createEfilingPlanTransaction(filingPlanData);

			const options: RazorpayOptions = {
				key: appConstants.razorpaySecretKey,
				amount: String(orderData.amount),
				currency: orderData.currency,
				order_id: orderData.id,
				name: "myTAXclub",
				description: "Assisted plan purchase",
				image: appConstants.appLogoUrl,
				handler: (res) => {
					setIsProcessing(() => EActionItem.NULL);
					handleOnPaymentSuccess();
					dispatch(
						showToastMsg({
							isVisible: true,
							title: "Success",
							message: "Your plan purchased successfully",
							variant: EToastvariant.Success
						})
					);
				},
				modal: {
					escape: false,
					confirm_close: true,
					ondismiss: () => {
						setIsProcessing(() => EActionItem.NULL);
						handleOnPaymentCancel();
					}
				},
				prefill: {
					name: loggedInUser?.fullName,
					email: loggedInUser?.email,
					contact: loggedInUser?.phoneNumber
				},
				theme: {
					color: "#5c56d6"
				}
			};

			await new Promise((r) => setTimeout(r, 500));
			const razorpay = new Razorpay(options);
			razorpay.open();

			razorpay.on("payment.failed", function (response: any) {
				dispatch(
					showToastMsg({
						isVisible: true,
						title: "Failed",
						message: response.error.description,
						variant: EToastvariant.Danger
					})
				);
			});
		} catch (error) {
			setIsProcessing(() => EActionItem.NULL);
			handleErrorOnInit();
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: "Unable to initiate payment process",
					variant: EToastvariant.Danger
				})
			);
		}
	}, [Razorpay]);

	useEffect(() => {
		if (loadRazorpay) {
			handlePayment();
		}
	}, [loadRazorpay, handlePayment]);

	return <>{isProcessing === EActionItem.PAYMENT && <OverlaySpinner />}</>;
};

export default RazorPayPayment;
